import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import { DataTableNumberColumn } from "~components/DataTable/components/DataTableNumberColumn/DataTableNumberColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import EntityColumn from "~pages/FinalStandings/components/EntityColumn";
import PersonColumn from "~pages/FinalStandings/components/PersonColumn";
import { FinalStandingsTableColumns } from "~pages/FinalStandings/enums/finalStandingsTableColumns.ts";
import { useFinalStandingsService } from "~pages/FinalStandings/hooks/useFinalStandingsService.ts";
import type { PlacingTableRow } from "~pages/FinalStandings/types/placingTableRow";
import type { Entity } from "~types/entity";
import type { Person } from "~types/person";
import { sortStringValues } from "~utils/utilities.ts";

const SORT_DIRECTIONS: SortOrder[] = ["ascend", "descend", "ascend"];

type EditablePlacingField = "resultPlace" | "points" | "prizeMoney";

const sortNumbers = (a?: number | null, b?: number | null) => {
  const valueA = a ?? Number.POSITIVE_INFINITY;
  const valueB = b ?? Number.POSITIVE_INFINITY;
  return valueA - valueB;
};

export const useGetFinalStandingsTableColumns = (massEditMode: boolean): Array<DataTableColumn<PlacingTableRow>> => {
  const { formatMessage } = useIntl();
  const { competitorType } = useSportConfiguration();
  const { upsertPlacing } = useFinalStandingsService();

  const handleSave = (row: PlacingTableRow, columnId: EditablePlacingField) => {
    return async (value: number | null) => {
      if (row[columnId] === value || (columnId === "resultPlace" && value == null)) {
        return;
      }

      await upsertPlacing({
        placingId: row.placingId,
        upsertData: {
          seasonId: row.seasonId,
          entityId: row.entityId,
          personId: row.personId,
          [columnId]: value,
        },
      });
    };
  };

  const entityColumn: DataTableColumn<PlacingTableRow> = {
    key: FinalStandingsTableColumns.Entity,
    title: formatMessage({ id: "teamname" }),
    dataIndex: "entity",
    width: 240,
    ellipsis: { showTitle: true },
    sorter: (a, b) => sortStringValues(a.entity?.nameFullLocal, b.entity?.nameFullLocal),
    render: (entity: Entity | undefined) => (entity ? <EntityColumn entity={entity} /> : null),
  };

  const personColumn: DataTableColumn<PlacingTableRow> = {
    key: FinalStandingsTableColumns.Person,
    title: formatMessage({ id: "person" }),
    dataIndex: "person",
    width: 240,
    ellipsis: { showTitle: true },
    sorter: (a, b) => sortStringValues(a.person?.nameFullLocal, b.person?.nameFullLocal),
    render: (person: Person | undefined) => (person ? <PersonColumn person={person} /> : null),
  };

  const entityIdColumn: DataTableColumn<PlacingTableRow> = {
    key: FinalStandingsTableColumns.EntityId,
    title: formatMessage({ id: "entity.id" }),
    dataIndex: "entityId",
    copyable: false,
    width: 260,
    sorter: (a, b) => sortStringValues(a.entityId, b.entityId),
  };

  const personIdColumn: DataTableColumn<PlacingTableRow> = {
    key: FinalStandingsTableColumns.PersonId,
    title: formatMessage({ id: "personId" }),
    dataIndex: "personId",
    copyable: false,
    width: 260,
    sorter: (a, b) => sortStringValues(a.personId, b.personId),
  };

  let competitorColumn: DataTableColumn<PlacingTableRow>;
  let competitorIdColumn: DataTableColumn<PlacingTableRow>;

  if (competitorType === CompetitorTypes.Entity) {
    competitorColumn = entityColumn;
    competitorIdColumn = entityIdColumn;
  } else if (competitorType === CompetitorTypes.Person) {
    competitorColumn = personColumn;
    competitorIdColumn = personIdColumn;
  } else {
    throw new Error(`Unsupported competitor type: ${competitorType}`);
  }

  return [
    competitorColumn,
    competitorIdColumn,
    {
      key: FinalStandingsTableColumns.Position,
      title: formatMessage({ id: "position" }),
      dataIndex: "position",
      width: 110,
      sorter: (a, b) => sortNumbers(a.position, b.position),
      sortDirections: SORT_DIRECTIONS,
    },
    {
      key: FinalStandingsTableColumns.ResultPlace,
      title: formatMessage({ id: "resultPlace" }),
      dataIndex: "resultPlace",
      width: 120,
      sorter: (a, b) => sortNumbers(a.resultPlace, b.resultPlace),
      sortDirections: SORT_DIRECTIONS,
      render: (value: number | null | undefined, row) => (
        <DataTableNumberColumn min={1} value={value} isEditMode={massEditMode} onSave={handleSave(row, "resultPlace")} />
      ),
    },
    {
      key: FinalStandingsTableColumns.Points,
      title: formatMessage({ id: "points" }),
      dataIndex: "points",
      width: 110,
      sorter: (a, b) => sortNumbers(a.points, b.points),
      sortDirections: SORT_DIRECTIONS,
      render: (value: number | null | undefined, row) => (
        <DataTableNumberColumn min={0} value={value} isEditMode={!!row.placingId && massEditMode} onSave={handleSave(row, "points")} />
      ),
    },
    {
      key: FinalStandingsTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: false,
      width: 180,
      sorter: (a, b) => sortStringValues(a.externalId, b.externalId),
    },
    {
      key: FinalStandingsTableColumns.PlacingId,
      title: formatMessage({ id: "placingId" }),
      dataIndex: "placingId",
      copyable: false,
      width: 260,
      sorter: (a, b) => sortStringValues(a.placingId, b.placingId),
    },
    {
      key: FinalStandingsTableColumns.PrizeMoney,
      title: formatMessage({ id: "prizeMoney" }),
      dataIndex: "prizeMoney",
      width: 130,
      sorter: (a, b) => sortNumbers(a.prizeMoney, b.prizeMoney),
      sortDirections: SORT_DIRECTIONS,
      render: (value: number | null | undefined, row) => (
        <DataTableNumberColumn min={0} value={value} isEditMode={!!row.placingId && massEditMode} onSave={handleSave(row, "prizeMoney")} />
      ),
    },
  ];
};
